import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Input from '../components/Input';
import Button from '../components/Button';
import Modal from '../components/Modal';
import { GatewayProvider, GatewayStatus, paymentGatewaysService } from '../services/paymentGatewaysService';
import { PAYMENT_WEBHOOKS_URL } from '../config/api';
import { formatInstant } from '../utils/dates';

interface CredentialField {
    name: string;        // Field name expected by ms-payments-integration
    label: string;
    placeholder: string;
    secret: boolean;     // Secrets are masked and never shown again after saving
}

const PROVIDERS: GatewayProvider[] = ['WOMPI', 'BOLD', 'MERCADOPAGO'];

const PROVIDER_CONFIG: Record<GatewayProvider, {
    name: string;
    title: string;
    logo: string;
    fields: CredentialField[];
    helperText?: string;
    // Payment notifications: a URL the organizer registers in the provider's dashboard
    webhook: { url?: string; instructions: React.ReactNode };
}> = {
    BOLD: {
        name: "Bold",
        title: "Configurar Bold",
        logo: "/Bold_logo.png",
        fields: [
            { name: 'identityKey', label: 'Llave de identidad', placeholder: 'Ej: 8a8b...', secret: false },
            { name: 'secretKey', label: 'Llave secreta', placeholder: 'Ej: 9f8e...', secret: true }
        ],
        helperText: "Recuerda que la llave secreta solo se muestra una vez al crearla en Bold.",
        webhook: {
            url: `${PAYMENT_WEBHOOKS_URL}/bold`,
            instructions: <>En Bold, ve a <strong>Integraciones</strong> &gt; <strong>Webhooks</strong>, agrega esta URL y suscríbela a los eventos de ventas y anulaciones.</>
        }
    },
    WOMPI: {
        name: "Wompi",
        title: "Configurar Wompi",
        logo: "/Wompi_logo.png",
        // Same order as the Wompi dashboard: API keys first, then technical integration secrets
        fields: [
            { name: 'publicKey', label: 'Llave pública', placeholder: 'Ej: pub_prod_...', secret: false },
            { name: 'privateKey', label: 'Llave privada', placeholder: 'Ej: prv_prod_...', secret: true },
            { name: 'eventsSecret', label: 'Secreto de eventos', placeholder: 'Ej: prod_...', secret: true },
            { name: 'integritySecret', label: 'Secreto de integridad', placeholder: 'Ej: prod_integrity_...', secret: true }
        ],
        helperText: "Las cuatro llaves deben ser del ambiente de Producción.",
        webhook: {
            url: `${PAYMENT_WEBHOOKS_URL}/wompi`,
            instructions: <>En el Dashboard de Wompi, ve a <strong>Desarrolladores</strong> y registra esta URL como <strong>URL de Eventos</strong> del mismo ambiente de tus llaves.</>
        }
    },
    MERCADOPAGO: {
        name: "MercadoPago",
        title: "Configurar MercadoPago",
        logo: "/Mercadopago_logo.png",
        fields: [
            { name: 'publicKey', label: 'Public Key', placeholder: 'Ej: APP_USR-...', secret: false },
            { name: 'accessToken', label: 'Access Token', placeholder: 'Ej: APP_USR-...', secret: true }
        ],
        webhook: {
            instructions: 'No necesitas configurar nada: SuperTicket le indica a MercadoPago a dónde notificar en cada pago.'
        }
    }
};

const emptyValues = (provider: GatewayProvider): Record<string, string> =>
    Object.fromEntries(PROVIDER_CONFIG[provider].fields.map(field => [field.name, '']));

const OrganizerPaymentGateways = () => {
    const { user, isAuthenticated, isLoading } = useAuth();
    const navigate = useNavigate();

    const [statusByProvider, setStatusByProvider] = useState<Partial<Record<GatewayProvider, GatewayStatus>>>({});
    const [isLoadingStatus, setIsLoadingStatus] = useState(true);
    const [statusError, setStatusError] = useState<string | null>(null);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentProvider, setCurrentProvider] = useState<GatewayProvider | null>(null);
    const [formValues, setFormValues] = useState<Record<string, string>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [copiedWebhook, setCopiedWebhook] = useState(false);

    useEffect(() => {
        if (!isLoading && isAuthenticated) {
            if (user?.role !== 'user-organizer') {
                navigate('/');
            }
        }
    }, [user, isAuthenticated, isLoading, navigate]);

    const loadStatus = useCallback(async () => {
        setIsLoadingStatus(true);
        setStatusError(null);
        try {
            const statuses = await paymentGatewaysService.getStatus();
            setStatusByProvider(Object.fromEntries(statuses.map(status => [status.provider, status])));
        } catch (error) {
            console.error('Error loading gateway status:', error);
            setStatusError('No fue posible consultar el estado de tus pasarelas. Recarga la página para intentarlo de nuevo.');
        } finally {
            setIsLoadingStatus(false);
        }
    }, []);

    useEffect(() => {
        if (!isLoading && user?.role === 'user-organizer') {
            loadStatus();
        }
    }, [isLoading, user, loadStatus]);

    const isConfigured = (provider: GatewayProvider) => Boolean(statusByProvider[provider]?.active);

    const handleOpenConfig = (provider: GatewayProvider) => {
        setCurrentProvider(provider);
        // Always start empty: stored secrets are never sent back to the browser
        setFormValues(emptyValues(provider));
        setSaveError(null);
        setCopiedWebhook(false);
        setIsModalOpen(true);
    };

    const handleCopyWebhook = async (url: string) => {
        try {
            await navigator.clipboard.writeText(url);
            setCopiedWebhook(true);
        } catch (error) {
            console.error('Could not copy the webhook URL:', error);
        }
    };

    const isFormComplete = currentProvider !== null
        && PROVIDER_CONFIG[currentProvider].fields.every(field => formValues[field.name]?.trim());

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!currentProvider || !isFormComplete) return;

        setIsSaving(true);
        setSaveError(null);
        try {
            const trimmed = Object.fromEntries(
                Object.entries(formValues).map(([name, value]) => [name, value.trim()]));
            await paymentGatewaysService.saveCredentials(currentProvider, trimmed);
            setIsModalOpen(false);
            await loadStatus();
        } catch (error) {
            setSaveError(error instanceof Error ? error.message : 'No fue posible guardar las credenciales.');
        } finally {
            setIsSaving(false);
        }
    };

    const formatDate = (value: string | null | undefined) =>
        formatInstant(value, { year: 'numeric', month: 'short', day: 'numeric' });

    const renderModalInstructions = (provider: GatewayProvider) => {
        switch (provider) {
            case 'BOLD':
                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                        <div>
                            <h4 className="font-bold text-[var(--st-text-primary)] mb-2">Integración con Bold</h4>
                            <p className="text-sm text-[var(--st-text-secondary)] mb-4">
                                Para procesar pagos con Bold, necesitamos tus llaves de integración.
                            </p>
                            <ol className="list-decimal list-inside text-sm text-[var(--st-text-secondary)] space-y-2">
                                <li>Ingresa a tu cuenta en <a href="https://bold.co" target="_blank" rel="noreferrer" className="text-[var(--st-accent-primary)] hover:underline">Bold.co</a>.</li>
                                <li>En el menú lateral izquierdo dirígete a <strong>Configuración</strong> &gt; <strong>Herramientas</strong> &gt; <strong>Integraciones</strong>.</li>
                                <li>Si aún no tienes Llaves de integración en <strong>Bold</strong>, selecciona la opción para generarlas por primera vez.</li>
                                <li>Copia la <strong>Llave de identidad</strong> y la <strong>Llave secreta</strong> de producción.</li>
                            </ol>
                        </div>
                        <div className="bg-[var(--st-bg-tertiary)] border-2 border-dashed border-[var(--st-border)] rounded-xl flex flex-col items-center justify-center p-8 text-[var(--st-text-muted)]">
                            <img src="/Bold_logo.png" alt="Bold Panel" className="w-24 h-auto opacity-80" />
                            <span className="text-xs mt-2 font-medium">Panel de Bold</span>
                        </div>
                    </div>
                );
            case 'WOMPI':
                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                        <div>
                            <h4 className="font-bold text-[var(--st-text-primary)] mb-2">Integración con Wompi</h4>
                            <p className="text-sm text-[var(--st-text-secondary)] mb-4">
                                Conecta tu cuenta Bancolombia a través de Wompi.
                            </p>
                            <ol className="list-decimal list-inside text-sm text-[var(--st-text-secondary)] space-y-2">
                                <li>Ingresa al Dashboard de Wompi.</li>
                                <li>Ve a la sección <strong>Desarrolladores</strong>.</li>
                                <li>Selecciona el ambiente de <strong>Producción</strong>.</li>
                                <li>En <strong>Llaves del API para integración técnica</strong>, copia la <strong>Llave pública</strong> y la <strong>Llave privada</strong>.</li>
                                <li>En <strong>Secretos para integración técnica</strong>, copia los secretos de <strong>Eventos</strong> y de <strong>Integridad</strong>.</li>
                            </ol>
                        </div>
                        <div className="bg-[var(--st-bg-tertiary)] border-2 border-dashed border-[var(--st-border)] rounded-xl flex flex-col items-center justify-center p-8 text-[var(--st-text-muted)]">
                            <img src="/Wompi_logo.png" alt="Wompi Panel" className="w-24 h-auto opacity-80" />
                            <span className="text-xs mt-2 font-medium">Dashboard Wompi</span>
                        </div>
                    </div>
                );
            case 'MERCADOPAGO':
                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                        <div>
                            <h4 className="font-bold text-[var(--st-text-primary)] mb-2">Integración MercadoPago</h4>
                            <p className="text-sm text-[var(--st-text-secondary)] mb-4">
                                Recibe pagos con la tecnología de MercadoLibre.
                            </p>
                            <ol className="list-decimal list-inside text-sm text-[var(--st-text-secondary)] space-y-2">
                                <li>Ve a <strong>Tus Integraciones</strong> en MercadoPago Developers.</li>
                                <li>Selecciona tu aplicación o crea una.</li>
                                <li>Copia la <strong>Public Key</strong> y el <strong>Access Token</strong> de las <strong>Credenciales de producción</strong>.</li>
                            </ol>
                        </div>
                        <div className="bg-[var(--st-bg-tertiary)] border-2 border-dashed border-[var(--st-border)] rounded-xl flex flex-col items-center justify-center p-8 text-[var(--st-text-muted)]">
                            <img src="/Mercadopago_logo.png" alt="MP Developers" className="w-24 h-auto opacity-80" />
                            <span className="text-xs mt-2 font-medium">MP Developers</span>
                        </div>
                    </div>
                );
            default: return null;
        }
    };

    if (isLoading || !user || user.role !== 'user-organizer') {
        return <div className="flex justify-center py-20"><div className="st-spinner st-spinner-lg"></div></div>;
    }

    return (
        <div className="st-page py-12">
            <div className="st-container max-w-4xl">

                <div className="mb-8">
                    <h1 className="font-display text-3xl font-bold text-[var(--st-text-primary)]">Pasarelas de pago</h1>
                    <br/>
                    <p className="text-[var(--st-text-secondary)]">
                        En esta sección podrás configurar tu pasarela o pasarelas de pago preferidas para recibir los
                        fondos de tus eventos. Es importante que configures al menos una opción para recibir los pagos de
                        forma inmediata en tu cuenta. Como recomendación particular sugerimos usar 'Bold' por su facilidad
                        de registro en caso de que aún no cuentes con el registro en alguna plataforma, sin embargo
                        todas las siguientes están soportadas.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                    {PROVIDERS.map((provider) => (
                        <div
                            key={provider}
                            className={`st-card p-6 flex flex-col items-center text-center transition-all hover:shadow-md ${provider === 'BOLD' ? 'border-l-4 border-l-[#EF4444]' : ''}`}
                        >
                            <div className="w-20 h-20 rounded-2xl bg-white flex items-center justify-center mb-4 shadow-sm border border-gray-100 p-2">
                                <img src={PROVIDER_CONFIG[provider].logo} alt={`Logo ${PROVIDER_CONFIG[provider].name}`} className="w-full h-full object-contain" />
                            </div>
                            <h3 className="font-display text-lg font-bold mb-2">{PROVIDER_CONFIG[provider].name}</h3>
                            <Button variant="secondary" size="sm" className="w-full" onClick={() => handleOpenConfig(provider)}>
                                {isConfigured(provider) ? 'Actualizar llaves' : 'Configurar'}
                            </Button>
                        </div>
                    ))}
                </div>

                <div className="st-card-static p-6 md:p-8">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-full bg-[var(--st-accent-secondary-light)] text-[var(--st-accent-secondary)] flex items-center justify-center font-bold">🔒</div>
                        <h2 className="font-display text-xl font-bold">Estado de Integraciones</h2>
                    </div>

                    {statusError && (
                        <p className="text-sm text-[var(--st-error)] mb-4">{statusError}</p>
                    )}

                    {isLoadingStatus ? (
                        <div className="flex justify-center py-6"><div className="st-spinner"></div></div>
                    ) : (
                        <div className="space-y-4">
                            {PROVIDERS.map((provider) => {
                                const status = statusByProvider[provider];
                                const withoutNotice = status?.paymentsConfirmedWithoutNotice ?? 0;
                                return (
                                    <div key={provider} className="p-4 bg-[var(--st-bg-tertiary)] rounded-lg">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-3 h-3 rounded-full ${isConfigured(provider) ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                                                <div className="text-left">
                                                    <span className="font-medium block">{PROVIDER_CONFIG[provider].name}</span>
                                                    {status?.publicKeyHint && (
                                                        <span className="text-xs text-[var(--st-text-muted)] font-mono">{status.publicKeyHint}</span>
                                                    )}
                                                </div>
                                            </div>
                                            <span className="text-sm text-[var(--st-text-muted)] text-right">
                                                {isConfigured(provider)
                                                    ? `Conectado${status?.lastUpdated ? ` · ${formatDate(status.lastUpdated)}` : ''}`
                                                    : 'Pendiente'}
                                            </span>
                                        </div>
                                        {withoutNotice > 0 && (
                                            <p className="mt-3 text-sm text-left text-[var(--st-text-secondary)]">
                                                <span className="st-badge st-badge-warning mr-2">Revisa el webhook</span>
                                                {withoutNotice === 1
                                                    ? 'En los últimos 30 días, 1 pago se confirmó sin que la pasarela nos avisara.'
                                                    : `En los últimos 30 días, ${withoutNotice} pagos se confirmaron sin que la pasarela nos avisara.`}
                                                {' '}Lo encontramos consultando la pasarela, pero las boletas pueden tardar varios minutos:
                                                {provider === 'MERCADOPAGO'
                                                    ? ' escríbenos para revisar la configuración.'
                                                    : ' registra la URL de webhooks de esta pasarela (Configurar).'}
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-4 mt-8">
                    <Button type="button" variant="ghost" onClick={() => navigate('/organizer/dashboard')}>
                        Volver
                    </Button>
                </div>

                {currentProvider && (
                    <Modal
                        isOpen={isModalOpen}
                        onClose={() => !isSaving && setIsModalOpen(false)}
                        title={PROVIDER_CONFIG[currentProvider].title}
                        className="max-w-4xl"
                    >
                        <form onSubmit={handleSave}>

                            {renderModalInstructions(currentProvider)}

                            <div className="bg-gray-50 border border-gray-100 rounded-xl p-6">
                                <h4 className="font-bold text-[var(--st-text-primary)] mb-4 border-b border-gray-200 pb-2">Credenciales</h4>

                                {isConfigured(currentProvider) && (
                                    <p className="text-xs text-[var(--st-text-muted)] mb-4">
                                        Ya tienes llaves guardadas para {PROVIDER_CONFIG[currentProvider].name}. Por seguridad no se
                                        muestran: al guardar, las nuevas reemplazan a las anteriores.
                                    </p>
                                )}

                                <div className="space-y-4">
                                    {PROVIDER_CONFIG[currentProvider].fields.map((field) => (
                                        <Input
                                            key={field.name}
                                            label={field.label}
                                            type={field.secret ? 'password' : 'text'}
                                            autoComplete="off"
                                            placeholder={field.placeholder}
                                            value={formValues[field.name] ?? ''}
                                            onChange={(e) => setFormValues(prev => ({ ...prev, [field.name]: e.target.value }))}
                                            required
                                            icon={field.secret ? (
                                                <svg className="w-5 h-5 text-[var(--st-text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                                            ) : undefined}
                                        />
                                    ))}

                                    {PROVIDER_CONFIG[currentProvider].helperText && (
                                        <p className="text-xs text-[var(--st-warning)] mt-2 flex items-center gap-1">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                            {PROVIDER_CONFIG[currentProvider].helperText}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="bg-gray-50 border border-gray-100 rounded-xl p-6 mt-4">
                                <h4 className="font-bold text-[var(--st-text-primary)] mb-2 border-b border-gray-200 pb-2">Notificaciones de pago (webhook)</h4>
                                <p className="text-sm text-[var(--st-text-secondary)] mb-3">
                                    {PROVIDER_CONFIG[currentProvider].webhook.instructions} Así confirmamos tus ventas aunque el
                                    comprador no regrese a SuperTicket después de pagar.
                                </p>
                                {PROVIDER_CONFIG[currentProvider].webhook.url && (
                                    <>
                                        <div className="flex flex-col sm:flex-row gap-2">
                                            <code className="flex-1 text-xs font-mono bg-white border border-[var(--st-border)] rounded-lg px-3 py-2 break-all">
                                                {PROVIDER_CONFIG[currentProvider].webhook.url}
                                            </code>
                                            <Button type="button" variant="secondary" size="sm"
                                                    onClick={() => handleCopyWebhook(PROVIDER_CONFIG[currentProvider].webhook.url!)}>
                                                {copiedWebhook ? 'Copiada' : 'Copiar URL'}
                                            </Button>
                                        </div>
                                        {!PROVIDER_CONFIG[currentProvider].webhook.url!.startsWith('https://') && (
                                            <p className="text-xs text-[var(--st-warning)] mt-2">
                                                Esta URL no es pública (HTTPS): la pasarela no podrá enviar notificaciones a este ambiente.
                                            </p>
                                        )}
                                    </>
                                )}
                            </div>

                            {saveError && (
                                <p className="text-sm text-[var(--st-error)] mt-4">{saveError}</p>
                            )}

                            <div className="flex justify-end gap-3 pt-6 mt-4 border-t border-[var(--st-border)]">
                                <Button type="button" variant="ghost" onClick={() => setIsModalOpen(false)} disabled={isSaving}>
                                    Cancelar
                                </Button>
                                <Button type="submit" className="st-btn st-btn-primary st-btn-md" variant="primary" disabled={!isFormComplete || isSaving}>
                                    {isSaving ? 'Guardando...' : 'Confirmar y Guardar'}
                                </Button>
                            </div>
                        </form>
                    </Modal>
                )}

            </div>
        </div>
    );
};

export default OrganizerPaymentGateways;
